import { Suspense, useCallback, useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import {
  useGetDriverStatus,
  useGetMonthConversion,
  useGetMonthGeographic,
  useGetMonthPassengerVSGood,
  useGetPhoneCallStatus,
  useGetTodayJourneyRank,
  useGetTodaySeatFillRate,
  useGetTodayStaffRank,
  useGetTodayOrders,
  useGetVehicleStatus,
} from 'queries/analytics.query';
import { Helmet } from 'react-helmet-async';
import {
  Avatar,
  Badge,
  Bleed,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Icon,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  ResourceItem,
  ResourceList,
  Text,
} from '@shopify/polaris';
import { convertSolarToLunar } from './amlich'; // Import đối tượng mặc định
import __helpers from 'helpers/index';
import { LayoutPopupIcon, MetafieldsFilledIcon, PriceListFilledIcon, ProfileIcon } from '@shopify/polaris-icons';
import RevenueItem from './components/revenueItem';
import { ReactECharts, ReactEChartsProps } from 'components/React-ECharts';
import RocketAnimation from 'media/lottie_files/Rocket.json';
import { useGetTodayTripManifest } from 'queries/analytics.query';
import TripManifestReport from './trip_manifest_report';
import DriverRevenueTimeline from './components/DriverRevenueTimeline';
import { useGetDriver_settlementDailyreport } from 'queries/driver_settlement.query';
import Capacity from 'components/Capacity';
import 'media/css/dashboard.scss';
import MonthlyStaffRanking from './components/BangXepHangNhanvienTheoThang';
import BangXepHangNhanVienTheoNgay from './components/BangXepHangNhanvienTheoNgay2';
import MeInAction from './components/MeWidget';
import UnExportInvoiceToday from './components/Unexport_invoice_today';

function LunarDateDisplay() {
  // 1. Lấy ngày dương lịch hiện tại
  const today = new Date();
  const day = today.getDate();
  const month = today.getMonth() + 1; // getMonth() trả về từ 0-11
  const year = today.getFullYear();

  // Định dạng chuỗi ngày dương
  const solarDateString = `Hôm nay ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;

  // 2. Chuyển đổi sang ngày âm lịch
  const lunarDate = convertSolarToLunar(day, month, year, 7);

  // 3. Lấy thông tin ngày âm và định dạng chuỗi
  const { lunarDay, lunarMonth, lunarYear } = lunarDate;
  const lunarDateString = `(tức  ${lunarDay} / ${lunarMonth} / ${lunarYear} Âm Lịch)`;

  return (
    <BlockStack>
      <Text variant="headingLg" as="h3">
        {solarDateString}
      </Text>
      <Text variant="bodyMd" as="p">
        {lunarDateString}
      </Text>
    </BlockStack>
  );
}

const BangXepHangDoanhThuTuyenTheoNgay = () => {
  const { data: todayJourneyRank, isLoading: loadingTodayJourneyRank } = useGetTodayJourneyRank();

  return (
    <Card>
      <Text as="h3" variant="headingMd">
        Doanh thu theo tuyến
      </Text>
      <br />
      <BlockStack gap={'400'}>
        {todayJourneyRank?.map((el) => {
          return (
            <InlineStack align="space-between">
              <BlockStack>
                <Text as="h4" variant="headingSm">
                  {el.journey_name}
                </Text>
                <Text as="p" variant="bodySm">
                  Tổng đơn: {el.order_count}
                </Text>
              </BlockStack>
              <Text as="h4" variant="headingMd">
                {__helpers.formatNumber(el.revenue)}
              </Text>
            </InlineStack>
          );
        })}
      </BlockStack>
    </Card>
  );
};

const TinhTrangXeTrongNgay = () => {
  const { data: vehicleStatusData, isLoading: loadingVehicleStatusData } = useGetVehicleStatus();

  const { BUSY_VEHICLE, FREE_VEHICLE } = vehicleStatusData ?? {};
  BUSY_VEHICLE?.sort((a, b) => (a?._count?.trip_manifest ?? 0) - (b?._count?.trip_manifest ?? 0));

  return (
    <Card roundedAbove="sm">
      <Text as="h2" variant="headingLg">
        Xe ngày hôm nay
      </Text>
      <br />
      <Box paddingBlockEnd="200">
        <BlockStack gap="200">
          {__helpers.isEmpty(BUSY_VEHICLE) ? (
            <Text as="p">Không có xe nào đang bận!</Text>
          ) : (
            BUSY_VEHICLE.map((el) => (
              <InlineStack gap="100" align="space-between" blockAlign="center" key={el.license_plate}>
                <Text fontWeight="bold" as="p">
                  {el.license_plate}
                </Text>
                <Badge tone="critical">{`${el._count.trip_manifest || 0} chuyến`}</Badge>
              </InlineStack>
            ))
          )}
        </BlockStack>
      </Box>
      <Bleed marginBlockEnd="400" marginInline="400">
        <Box background="bg-surface-secondary" padding="400">
          <BlockStack gap="200">
            <Text as="h3" variant="headingLg" fontWeight="medium">
              Xe chưa vào chuyến
            </Text>
            {__helpers.isEmpty(FREE_VEHICLE) ? (
              <Text as="p">Không có xe nào chưa được phân công!</Text>
            ) : (
              FREE_VEHICLE.map((el) => (
                <InlineStack gap="100" align="space-between" blockAlign="center" key={el.license_plate}>
                  <Text fontWeight="bold" as="span">
                    {el.license_plate}
                  </Text>
                  <Text tone="subdued" as="span">
                    {el.name}
                  </Text>
                </InlineStack>
              ))
            )}
          </BlockStack>
        </Box>
      </Bleed>
    </Card>
  );
};

const TinhTrangLaiXeTrongNgay = () => {
  const { data: driverStatusData, isLoading: loadingDriverStatusData } = useGetDriverStatus();

  const { BUSY_DRIVER, FREE_DRIVER } = driverStatusData ?? {};

  BUSY_DRIVER?.sort((a, b) => (a?._count?.trip_manifest_driver ?? 0) - (b?._count?.trip_manifest_driver ?? 0));

  return (
    <Card roundedAbove="sm">
      <Text as="h2" variant="headingLg">
        Tài xế đã có chuyến
      </Text>
      <br />

      <Box paddingBlockEnd="200">
        <BlockStack gap="200">
          {__helpers.isEmpty(BUSY_DRIVER) ? (
            <Text as="p">Không có tài xế nào đang bận!</Text>
          ) : (
            BUSY_DRIVER.map((el, index) => (
              <InlineStack gap="100" align="space-between" blockAlign="center" key={el.user_id + '__TinhTrangLaiXeTrongNgay_taixeBusy' + index}>
                <Text fontWeight="bold" as="p">
                  {el.display_name}
                </Text>
                <Badge tone="critical">{`${el._count.trip_manifest_driver || 0} chuyến`}</Badge>
              </InlineStack>
            ))
          )}
        </BlockStack>
      </Box>
      <Bleed marginBlockEnd="400" marginInline="400">
        <Box background="bg-surface-secondary" padding="400">
          <BlockStack gap="200">
            <Text as="h3" variant="headingLg" fontWeight="medium">
              Tài xế chưa có chuyến
            </Text>
            {__helpers.isEmpty(FREE_DRIVER) ? (
              <Text as="p">Không có tài xế nào chưa được phân công!</Text>
            ) : (
              FREE_DRIVER.map((el, index) => (
                <InlineStack gap="100" align="space-between" blockAlign="center" key={el.user_id + '__TinhTrangLaiXeTrongNgay_taixeFree' + index}>
                  <Text fontWeight="bold" as="p">
                    {el.display_name}
                  </Text>
                  <Text as="p" tone="subdued">
                    {el.user_phonenumber}
                  </Text>
                </InlineStack>
              ))
            )}
          </BlockStack>
        </Box>
      </Bleed>
    </Card>
  );
};

const TongdaiHomNay = () => {
  const { data: phonelogData, isLoading } = useGetPhoneCallStatus();
  return (
    <Card>
      <BlockStack gap={'200'}>
        <Text as="h3" variant="headingMd">
          Tổng đài hôm nay
        </Text>
        {__helpers.isEmpty(phonelogData) ? (
          <Text as="p" alignment="center" tone="subdued">
            Chưa có cuộc gọi nào!
          </Text>
        ) : (
          Object.keys(phonelogData).map((al, index) => {
            return (
              <InlineStack align="space-between" blockAlign="center" key={index + '____tongdaidm'}>
                <Text as="p">{al}</Text>
                <Badge tone="success-strong">{`${phonelogData[al]} cuộc`}</Badge>
              </InlineStack>
            );
          })
        )}
      </BlockStack>
    </Card>
  );
};

const TinhHinhThutienHomnayDriverSettlementDailyReportWidget = () => {
  const { data, isLoading } = useGetDriver_settlementDailyreport();
  const [entity, setEntity] = useState([]);
  useEffect(() => {
    if (data) {
      setEntity(data);
    }
  }, [data]);
  return (
    <Card roundedAbove="sm">
      <Text as="h2" variant="headingLg">
        Tài chính hôm nay
      </Text>
      <br />
      <ResourceList
        loading={isLoading}
        items={entity}
        emptyState={
          <BlockStack>
            <Text as="p" tone="subdued">
              Chưa có thông tin nào ở đây!
            </Text>
          </BlockStack>
        }
        renderItem={(item) => {
          const { user_id, display_name, total_trip_manifest, total_amount } = item;

          return (
            <ResourceItem id={user_id} url={null} media={<Icon source={ProfileIcon} />}>
              <InlineStack align="space-between" blockAlign="center">
                <div>
                  <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {display_name}
                  </Text>
                  <div>{total_trip_manifest} issues</div>
                </div>
                <Text as="span" variant="headingLg">
                  {__helpers.formatNumberWithCommas(total_amount)}
                </Text>
              </InlineStack>
            </ResourceItem>
          );
        }}
      />
    </Card>
  );
};

export default function NewDashboard() {
  const { data: todayTripManifestData, isLoading: loadingTodayTripManifestData } = useGetTodayTripManifest();
  const { data: todayOrderData, isLoading: loadingTodayOrderData } = useGetTodayOrders();
  const { data: todaySeatFillRateData, isLoading: loadingTodaySeatFillRateData } = useGetTodaySeatFillRate();
  const { data: todayMonthConversionData, isLoading: loadingTodayMonthConversionData } = useGetMonthConversion();
  const { data: monthGeographicData, isLoading: loadingMonthGeographicData } = useGetMonthGeographic();
  const { data: monthPassengerVsGoodRateData } = useGetMonthPassengerVSGood();

  const totalSaleViaAllChanelOption: ReactEChartsProps['option'] = {
    tooltip: {
      trigger: 'axis',
    },
    legend: {
      top: 5,
      left: 'center',
      itemGap: 8, // giảm khoảng cách giữa các item
      padding: 0, // bỏ padding mặc định
    },
    // grid: {
    //   top: 30, // khoảng cách trên cùng
    //   right: 5, // khoảng cách bên phải
    //   bottom: 5, // khoảng cách dưới
    //   left: 5, // khoảng cách bên trái
    //   containLabel: true, // để label không bị cắt
    // },
    grid: {
      top: 28,
      right: 8,
      bottom: 2,
      left: 8,
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: todayMonthConversionData?.labels || [],
        axisPointer: {
          type: 'shadow',
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: '' /** cột đứng bên tay phải */,
        alignTicks: true,
        axisLabel: {
          formatter: '{value}',
        },
      },
    ],
    series: [
      {
        name: 'Số đơn',
        type: 'bar',
        // prettier-ignore
        data: todayMonthConversionData?.orderData || [],
        barMaxWidth: 16,
      },
      {
        name: 'Số cuộc gọi',
        type: 'bar',
        // prettier-ignore
        data: todayMonthConversionData?.phoneData || [],
        barMaxWidth: 16,
      },
    ],
  };

  let PassengerVsGoodChartDataSet = monthPassengerVsGoodRateData || [];
  PassengerVsGoodChartDataSet.push({
    // make an record to fill the bottom 50%
    value: PassengerVsGoodChartDataSet.reduce((acc, cur) => acc + cur.value, 0),
    itemStyle: {
      // stop the chart from rendering this piece
      color: 'none',
      decal: {
        symbol: 'none',
      },
    },
    label: {
      show: false,
    },
  });

  const PassengerVsGoodChart: ReactEChartsProps['option'] = {
    title: {
      text: 'Tỉ lệ hàng/hành khách',
      subtext: 'Trong 30 ngày gần nhất',
      top: 0,
    },
    tooltip: {
      trigger: 'item',
    },
    legend: {
      bottom: '0%',
      left: 'center',
      selectedMode: false,
    },
    series: [
      {
        name: 'Số đơn',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '70%'],
        // adjust the start angle
        startAngle: 180,
        label: {
          show: true,
          formatter(param) {
            // correct the percentage
            return param.name + ' (' + param.percent * 2 + '%)';
          },
        },
        data: PassengerVsGoodChartDataSet,
      },
    ],
  };

  const SimpleEncode: ReactEChartsProps['option'] = {
    title: {
      text: 'Phân bố khách hàng',
      subtext: 'Trong 30 ngày gần nhất, theo địa giới hành chính',
      textStyle: {
        fontSize: 14,
        align: 'center',
      },
      subtextStyle: {
        align: 'center',
      },
    },
    series: {
      type: 'sunburst',
      data: monthGeographicData || {},
      radius: [0, '95%'],
      sort: undefined,
      emphasis: {
        focus: 'ancestor',
      },
      levels: [
        {},
        {
          r0: '15%',
          r: '35%',
          itemStyle: {
            borderWidth: 2,
          },
          label: {
            rotate: 'tangential',
          },
        },
        {
          r0: '35%',
          r: '70%',
          label: {
            align: 'right',
          },
        },
        {
          r0: '70%',
          r: '72%',
          label: {
            position: 'outside',
            padding: 3,
            silent: false,
          },
          itemStyle: {
            borderWidth: 3,
          },
        },
      ],
    },
  };

  return (
    <>
      <Helmet>
        <title>Trang chủ</title>
      </Helmet>
      <Page fullWidth>
        <LunarDateDisplay />
        <br />

        <Grid
          columns={{ xs: 1, sm: 1, md: 2, lg: 6, xl: 6 }}
          areas={{
            xs: ['column_1', 'column_2'],
            sm: ['column_1', 'column_2'],
            md: ['column_1 column_2'],
            // md: ['column_1 column_3 column_3 column_2'],
            // lg: ['column_1 column_2 column_3 column_3 column_3 column_3'],
            lg: [' column_1 column_1  column_2 column_2 column_2 column_2'],
            xl: [' column_1 column_1  column_2 column_2 column_2 column_2'],
          }}
        >
          <Grid.Cell area="column_1">
            <MeInAction />

            <Capacity current_user_can={['manage_invoice', 'manage_driver_settlement']}>
              <br />
              <Divider />
              <br />
              <UnExportInvoiceToday />
            </Capacity>
          </Grid.Cell>
          <Grid.Cell area="column_2">
            <Layout>
              <Layout.Section variant="oneThird">
                <RevenueItem
                  loading={loadingTodayTripManifestData}
                  tone="success"
                  title="Tổng số chuyến"
                  value={todayTripManifestData?.TOTAL || 0}
                  icon={
                    <span style={{ width: '30px' }}>
                      <Lottie animationData={RocketAnimation} loop />
                    </span>
                  }
                  subInfo={
                    <InlineGrid gap="300" columns={3}>
                      <BlockStack>
                        <Text as="h2" alignment="center" fontWeight="bold">
                          Hoàn thành
                        </Text>
                        <Text as="h2" alignment="center" fontWeight="bold">
                          {todayTripManifestData?.FINALIZED || 0}
                        </Text>
                      </BlockStack>
                      <BlockStack>
                        <Text as="h2" alignment="center" fontWeight="bold">
                          Đang chạy
                        </Text>
                        <Text as="h2" alignment="center" fontWeight="bold">
                          {todayTripManifestData?.IN_TRANSIT + (todayTripManifestData?.COMPLETED || 0)}
                        </Text>
                      </BlockStack>
                    </InlineGrid>
                  }
                />
              </Layout.Section>

              <Layout.Section variant="oneThird">
                <RevenueItem
                  loading={loadingTodayOrderData}
                  title="Đơn hôm nay"
                  value={todayOrderData?.TOTAL || 0}
                  icon={<PriceListFilledIcon width={24} />}
                  subInfo={`Tỉ lệ hủy: ${todayOrderData?.TOTAL ? Math.floor((todayOrderData?.CANCELLED / todayOrderData?.TOTAL) * 100) : 0}% `}
                  progress={Math.floor(((todayOrderData?.TOTAL - todayOrderData?.CANCELLED) / todayOrderData?.TOTAL) * 100)}
                />
              </Layout.Section>
              <Layout.Section variant="oneThird">
                <RevenueItem
                  loading={loadingTodaySeatFillRateData}
                  title="Tỷ lệ lấp đầy ghế"
                  value={`${todaySeatFillRateData?.TODAY || 0}%`}
                  icon={<MetafieldsFilledIcon width={24} />}
                  subInfo={`So với hôm qua: ${todaySeatFillRateData?.PERCENTCHANGE}%`}
                  progress={todaySeatFillRateData?.TODAY}
                />
              </Layout.Section>
            </Layout>
            <br />
            <Divider />
            <br />
            <BangXepHangNhanVienTheoNgay />
            <br />
            <MonthlyStaffRanking />
          </Grid.Cell>
        </Grid>

        <br />
        <Divider />
        <br />

        <ReactECharts
          option={totalSaleViaAllChanelOption}
          style={{ width: '100%', minHeight: '350px', maxHeight: '500px', padding: 0 }}
          graphic={undefined}
        />
        <br />
        <Suspense>
          <TripManifestReport />
          <Capacity current_user_can={'view_finance_widget'}>
            <DriverRevenueTimeline />
          </Capacity>
        </Suspense>
        <br />
        <br />
        <Grid
          columns={{ xs: 1, sm: 4, md: 4, lg: 6, xl: 6 }}
          areas={{
            xs: ['column_1', 'column_2', 'column_3'],
            sm: ['column_1 column_1 column_2 column_2', 'column_3 column_3 column_3 column_3'],
            md: ['column_1 column_1 column_2 column_2', 'column_3 column_3 column_3 column_3'],
            // md: ['column_1 column_3 column_3 column_2'],
            // lg: ['column_1 column_2 column_3 column_3 column_3 column_3'],
            lg: [' column_1 column_1 column_2 column_2 column_3 column_3'],
            xl: [' column_1 column_1 column_2 column_2 column_3 column_3'],
          }}
        >
          <Grid.Cell area="column_1">
            <BlockStack gap="400">
              <Capacity current_user_can={'view_finance_widget'}>
                <BangXepHangDoanhThuTuyenTheoNgay />
              </Capacity>
              <Capacity current_user_can={'view_finance_widget'}>
                <TinhHinhThutienHomnayDriverSettlementDailyReportWidget />
              </Capacity>
            </BlockStack>
          </Grid.Cell>
          <Grid.Cell area="column_2">
            <TongdaiHomNay />
            <br />
            <TinhTrangXeTrongNgay />
            <br />
            <TinhTrangLaiXeTrongNgay />
          </Grid.Cell>
          <Grid.Cell area="column_3">
            <ReactECharts option={PassengerVsGoodChart} style={{ minHeight: '300px', padding: '15px' }} />
            <ReactECharts option={SimpleEncode} style={{ minHeight: '350px', padding: '15px' }} />
          </Grid.Cell>
        </Grid>
      </Page>
    </>
  );
}

